import { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Building2, FileText, Hash, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { formatNumber, cn } from '../lib/utils';
import * as api from '../lib/api';

function todayIso() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/**
 * CHF reporting remaining from aggregation / breakdown SSOT.
 * Prefer remaining_chf_reporting; never re-derive as total cost − paid in the UI.
 */
export function vendorRemainingChf(row) {
  return Number(row.remaining_chf_reporting ?? row.remaining_chf ?? 0);
}

/**
 * Per-deal breakdown row: CHF reporting vs settlement/native (same rules as DealVendorPaymentsTab).
 * `showChfReference === true` → CHF reporting columns.
 */
function breakdownAmountCells(row, showChfReference) {
  if (showChfReference) {
    return {
      total: Number(row.total_cost_chf_reporting ?? row.total_cost_chf ?? 0),
      paid: Number(row.paid_chf_reporting ?? row.paid_chf ?? 0),
      rem: vendorRemainingChf(row),
      cur: 'CHF',
    };
  }
  if (row.settlement_currency && row.total_cost_vendor != null) {
    const paidV = Number(row.paid_vendor ?? 0);
    const remV =
      row.remaining_vendor != null
        ? Number(row.remaining_vendor)
        : Number(row.total_cost_vendor) - paidV;
    return {
      total: Number(row.total_cost_vendor),
      paid: paidV,
      rem: remV,
      cur: row.settlement_currency,
    };
  }
  const cc = row.core_currency || 'CHF';
  return {
    total: Number(row.total_cost_chf ?? 0),
    paid: Number(row.paid_chf ?? 0),
    rem: Number(row.remaining_chf ?? 0),
    cur: cc,
  };
}

/** Header KPIs: single settlement currency across deals, else CHF reporting + optional note. */
function aggregateVendorDetailHeader(bd, showChfReference) {
  if (!bd.length) {
    return { total: 0, paid: 0, rem: 0, cur: 'CHF', note: null };
  }
  if (showChfReference) {
    const total = bd.reduce((s, r) => s + Number(r.total_cost_chf_reporting ?? r.total_cost_chf ?? 0), 0);
    const paid = bd.reduce((s, r) => s + Number(r.paid_chf_reporting ?? r.paid_chf ?? 0), 0);
    const rem = bd.reduce((s, r) => s + vendorRemainingChf(r), 0);
    return { total, paid, rem, cur: 'CHF', note: null };
  }
  const locked = bd.filter((r) => r.settlement_currency && r.total_cost_vendor != null);
  const curs = new Set(locked.map((r) => r.settlement_currency));
  if (locked.length === bd.length && curs.size === 1) {
    const cur = [...curs][0];
    const total = locked.reduce((s, r) => s + Number(r.total_cost_vendor), 0);
    const paid = locked.reduce((s, r) => s + Number(r.paid_vendor ?? 0), 0);
    const rem = locked.reduce((s, r) => s + Number(r.remaining_vendor ?? 0), 0);
    return { total, paid, rem, cur, note: null };
  }
  const total = bd.reduce((s, r) => s + Number(r.total_cost_chf_reporting ?? r.total_cost_chf ?? 0), 0);
  const paid = bd.reduce((s, r) => s + Number(r.paid_chf_reporting ?? r.paid_chf ?? 0), 0);
  const rem = bd.reduce((s, r) => s + vendorRemainingChf(r), 0);
  let note = null;
  if (locked.length < bd.length) {
    note = 'Some deals have no locked settlement currency — CHF reporting totals.';
  } else if (curs.size > 1) {
    note = 'Mixed settlement currencies across deals — CHF reporting totals.';
  }
  return { total, paid, rem, cur: 'CHF', note };
}

/** CHF reporting amounts for a deals-breakdown row (always from reporting fields when present). */
function chfReportingAmounts(row) {
  return {
    total: Number(row.total_cost_chf_reporting ?? row.total_cost_chf ?? 0),
    paid: Number(row.paid_chf_reporting ?? row.paid_chf ?? 0),
    rem: vendorRemainingChf(row),
  };
}

/** Locked settlement amounts, or null if this vendor is not settled on the deal. */
function vendorSettlementAmounts(row) {
  if (!row.settlement_currency || row.total_cost_vendor == null) return null;
  const paidV = Number(row.paid_vendor ?? 0);
  const remV =
    row.remaining_vendor != null
      ? Number(row.remaining_vendor)
      : Number(row.total_cost_vendor) - paidV;
  return {
    total: Number(row.total_cost_vendor),
    paid: paidV,
    rem: remV,
    cur: row.settlement_currency,
  };
}

/** Deal dropdown + “remaining” for Log payment: settlement currency when locked, else CHF reporting. */
function dealRemainingForPaymentLog(d) {
  if (!d) return '';
  const v = vendorSettlementAmounts(d);
  if (v) {
    return `${formatNumber(v.rem, 2)} ${v.cur}`;
  }
  const chf = chfReportingAmounts(d);
  return `${formatNumber(chf.rem, 2)} CHF`;
}

/** Deal total revenue: core currency primary, CHF reporting subline when different. */
function ByDealRevenueCell({ row }) {
  const core = row.core_currency || 'CHF';
  const revCore = Number(row.total_revenue_chf ?? 0);
  const revChf = Number(row.total_revenue_chf_reporting ?? revCore);
  const chfLine = `${formatNumber(revChf, 2)} CHF`;
  if (core !== 'CHF') {
    return (
      <div className="flex flex-col items-end gap-0.5 tabular-nums leading-tight">
        <span className="font-semibold text-gray-900">
          {formatNumber(revCore, 2)} {core}
        </span>
        <span className="text-[11px] font-normal text-gray-500">{chfLine}</span>
      </div>
    );
  }
  return <span className="tabular-nums text-gray-900">{chfLine}</span>;
}

/** By deal table: primary line = vendor settlement when locked; second line = CHF reporting. */
function ByDealDualAmountCell({ row, kind }) {
  const chf = chfReportingAmounts(row)[kind];
  const v = vendorSettlementAmounts(row);
  const chfLine = `${formatNumber(chf, 2)} CHF`;
  if (v) {
    const vNum = kind === 'total' ? v.total : kind === 'paid' ? v.paid : v.rem;
    return (
      <div className="flex flex-col items-end gap-0.5 tabular-nums leading-tight">
        <span className="font-semibold text-gray-900">
          {formatNumber(vNum, 2)} {v.cur}
        </span>
        <span className="text-[11px] font-normal text-gray-500">{chfLine}</span>
      </div>
    );
  }
  return <span className="tabular-nums text-gray-900">{chfLine}</span>;
}

function formatIsoDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// ---------------------------------------------------------------------------
// VendorPaymentForm
// ---------------------------------------------------------------------------
// Inline form to log a vendor payment from the general Vendor detail page.
// Mirrors the per-deal form in DealVendorPaymentsTab (same fields, same API)
// but adds an explicit "Deal" selector because one vendor can span many deals.
//
// The deal list is sourced from `dealBreakdown` (already loaded by the parent)
// — that way the user can only attribute a payment to a deal that actually
// uses this vendor, which matches the backend invariant.
function VendorPaymentForm({
  vendorName,
  deals,
  onLogged,
  showChfReference,
  onToggleChfReference,
  onOpenDealVendorPayments,
}) {
  const [form, setForm] = useState(() => ({
    deal_id: deals[0]?.deal_id || '',
    amount_input: '',
    payment_date: todayIso(),
    note: '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm((f) => {
      if (f.deal_id && deals.some((d) => d.deal_id === f.deal_id)) return f;
      const sorted = [...deals].sort((a, b) => {
        const ra = chfReportingAmounts(a).rem;
        const rb = chfReportingAmounts(b).rem;
        return rb - ra;
      });
      return { ...f, deal_id: sorted[0]?.deal_id || '' };
    });
  }, [deals]);

  const selectedDeal = useMemo(
    () => deals.find((d) => d.deal_id === form.deal_id) || null,
    [deals, form.deal_id],
  );

  const settlement = selectedDeal?.settlement_currency;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const amt = Number(String(form.amount_input).replace(/'/g, '').replace(',', '.'));
    if (!form.deal_id) {
      setError('Select which deal this payment belongs to.');
      return;
    }
    if (!settlement) {
      setError('Open that deal → Vendor Payments and lock a settlement currency before logging.');
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      setError(`Enter a positive amount in ${settlement}.`);
      return;
    }
    setBusy(true);
    try {
      await api.addDealVendorPayment(form.deal_id, {
        vendor_name: vendorName,
        amount: amt,
        payment_date: form.payment_date,
        note: (form.note || '').trim(),
      });
      setForm((f) => ({
        ...f,
        amount_input: '',
        note: '',
        payment_date: todayIso(),
      }));
      if (onLogged) await onLogged();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!deals.length) {
    return (
      <div className="rounded-md border border-dashed border-gray-200 bg-gray-50/60 px-4 py-3 text-xs text-gray-500">
        No Approved+ pipeline deals use this vendor yet — nothing to log a payment against.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 sm:p-4"
      data-testid="vendor-global-payment-form"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Log payment</h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={() => onToggleChfReference?.()}
            data-testid="vendor-global-payment-chf-toggle"
          >
            {showChfReference ? 'Vendor currency' : 'CHF (reporting)'}
          </Button>
        </div>
        {selectedDeal ? (
          <span className="text-[11px] text-gray-400">
            Remaining on selected deal:{' '}
            <span className="font-mono font-semibold tabular-nums text-gray-700">
              {dealRemainingForPaymentLog(selectedDeal)}
            </span>
          </span>
        ) : null}
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="flex min-w-[180px] flex-[2] flex-col gap-1 text-xs text-gray-600">
          Deal
          <select
            className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px] text-gray-900"
            value={form.deal_id}
            onChange={(e) => setForm((f) => ({ ...f, deal_id: e.target.value }))}
            disabled={busy}
            data-testid="vendor-global-payment-deal"
          >
            {deals.map((d) => (
              <option key={d.deal_id} value={d.deal_id}>
                {d.deal_name} — {dealRemainingForPaymentLog(d)} left
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-[120px] flex-1 flex-col gap-1 text-xs text-gray-600">
          {settlement ? `Amount (${settlement})` : 'Amount (deal not ready)'}
          <input
            type="text"
            inputMode="decimal"
            className="rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-[12px] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            value={form.amount_input}
            onChange={(e) => setForm((f) => ({ ...f, amount_input: e.target.value }))}
            placeholder={settlement ? '0' : '—'}
            disabled={busy || !settlement}
            title={
              settlement
                ? undefined
                : 'Lock a settlement currency for this vendor on the selected deal (Deal → Vendor payments) before amounts can be entered.'
            }
            data-testid="vendor-global-payment-amount"
          />
        </label>
        <label className="flex min-w-[140px] flex-1 flex-col gap-1 text-xs text-gray-600">
          Date
          <input
            type="date"
            className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px]"
            value={form.payment_date}
            onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
            disabled={busy}
            data-testid="vendor-global-payment-date"
          />
        </label>
        <label className="flex min-w-[160px] flex-[2] flex-col gap-1 text-xs text-gray-600">
          Note (optional)
          <input
            type="text"
            className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[12px]"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            disabled={busy}
            data-testid="vendor-global-payment-note"
          />
        </label>
        <Button
          type="submit"
          disabled={busy || !form.deal_id || !settlement}
          className="h-8 px-3 text-xs sm:mb-0"
          data-testid="vendor-global-payment-submit"
        >
          {busy ? 'Saving…' : 'Add payment'}
        </Button>
      </div>
      {!settlement && selectedDeal ? (
        <div className="mt-2 flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-950 sm:flex-row sm:items-center sm:justify-between">
          <p className="min-w-0 leading-snug">
            Amount stays disabled until this deal has a <strong>locked settlement currency</strong> for this vendor.
            Open the deal, go to <strong>Vendor payments</strong>, pick CHF / EUR / USD, then <strong>Lock</strong>.
          </p>
          {onOpenDealVendorPayments ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 border-amber-300 bg-white text-[11px] text-amber-950 hover:bg-amber-100"
              onClick={() => onOpenDealVendorPayments(selectedDeal.deal_id)}
              data-testid="vendor-global-payment-open-deal-lock"
            >
              Open deal → Vendor payments
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-800">
          {error}
        </div>
      ) : null}
    </form>
  );
}

function VendorListView({ vendors, onSelectVendor, loading }) {
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
        Loading vendor data...
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3 p-12">
        <Building2 className="h-10 w-10 text-gray-300" />
        <p className="text-sm">No vendors assigned yet</p>
        <p className="text-xs text-gray-300">Tag line item costs with vendors in the Deals tab</p>
      </div>
    );
  }

  const costChfRep = (v) => Number(v.total_cost_chf_reporting ?? v.total_cost_chf ?? 0);
  const totalCost = vendors.reduce((sum, v) => sum + costChfRep(v), 0);
  const totalPaid = vendors.reduce((sum, v) => sum + Number(v.paid_chf_reporting ?? v.paid_chf ?? 0), 0);
  const totalRemaining = vendors.reduce((sum, v) => sum + vendorRemainingChf(v), 0);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-4 border-b border-gray-100 bg-gray-50/50 px-4 py-3 sm:gap-8 sm:px-6 sm:py-4">
        <div>
          <span className="pl-kpi-label">Vendors</span>
          <p className="pl-kpi-value" data-testid="vendor-total-count">{vendors.length}</p>
        </div>
        <div>
          <span className="pl-kpi-label">Total Cost</span>
          <p className="pl-kpi-value" data-testid="vendor-total-cost">{formatNumber(totalCost)} CHF</p>
        </div>
        <div>
          <span className="pl-kpi-label">Paid (logged)</span>
          <p className="pl-kpi-value" data-testid="vendor-total-paid">{formatNumber(totalPaid)} CHF</p>
        </div>
        <div>
          <span className="pl-kpi-label">Remaining (to pay)</span>
          <p className="pl-kpi-value" data-testid="vendor-total-remaining">{formatNumber(totalRemaining)} CHF</p>
        </div>
      </div>

      {/* Table */}
      <div className="min-w-0 flex-1 overflow-auto p-4 sm:p-6">
        <div className="space-y-3 md:hidden">
          {vendors.map((v) => {
            const tc = costChfRep(v);
            const costShare = totalCost > 0 ? (tc / totalCost) * 100 : 0;
            const paid = Number(v.paid_chf_reporting ?? v.paid_chf ?? 0);
            const rem = vendorRemainingChf(v);
            return (
              <button
                key={v.vendor_name}
                type="button"
                className="w-full rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm active:bg-gray-50"
                onClick={() => onSelectVendor(v.vendor_name)}
                data-testid={`vendor-row-${v.vendor_name}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100">
                        <Building2 className="h-4 w-4 text-gray-500" />
                      </div>
                      <span className="truncate text-sm font-semibold text-gray-900">{v.vendor_name}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200">
                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${costShare}%` }} />
                      </div>
                      <span className="font-mono text-xs text-gray-400">{costShare.toFixed(0)}%</span>
                    </div>
                  </div>
                  <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-300" />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt className="text-gray-400">Total cost</dt>
                    <dd className="font-mono text-gray-900">{formatNumber(tc)} CHF</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">Paid</dt>
                    <dd className="font-mono text-gray-900">{formatNumber(paid)} CHF</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">Remaining</dt>
                    <dd className="font-mono text-gray-900">{formatNumber(rem)} CHF</dd>
                  </div>
                  <div className="flex items-end justify-between text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5 text-gray-400" />
                      {v.deal_count}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Hash className="h-3.5 w-3.5 text-gray-400" />
                      {v.line_item_count}
                    </span>
                  </div>
                </dl>
              </button>
            );
          })}
        </div>

        <table className="pl-table-shell hidden w-full max-w-5xl min-w-0 border-separate border-spacing-0 md:table" data-testid="vendor-list-table">
          <thead className="pl-table-header">
            <tr className="bg-white">
              <th className="pl-table-head-cell text-left">Vendor</th>
              <th className="pl-table-head-cell text-right">Total Cost</th>
              <th className="pl-table-head-cell text-right">Paid</th>
              <th className="pl-table-head-cell text-right">Remaining</th>
              <th className="pl-table-head-cell text-center">Deals</th>
              <th className="pl-table-head-cell text-center">Line Items</th>
              <th className="pl-table-head-cell w-10 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => {
              const tc = costChfRep(v);
              const costShare = totalCost > 0 ? (tc / totalCost) * 100 : 0;
              const paid = Number(v.paid_chf_reporting ?? v.paid_chf ?? 0);
              const rem = vendorRemainingChf(v);
              return (
                <tr
                  key={v.vendor_name}
                  className="pl-row border-b border-gray-50 transition-colors hover:bg-gray-50/80 cursor-pointer"
                  onClick={() => onSelectVendor(v.vendor_name)}
                  data-testid={`vendor-row-desktop-${v.vendor_name}`}
                >
                  <td className="pl-cell-text">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-md bg-gray-100 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-gray-500" />
                      </div>
                      <div>
                        <span className="font-medium text-sm text-gray-900">{v.vendor_name}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <div className="h-1.5 rounded-full bg-gray-200 w-20 overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${costShare}%` }} />
                          </div>
                          <span className="text-xs text-gray-400 tabular-nums">{costShare.toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="pl-cell-num text-[13px] font-semibold text-gray-900">
                    {formatNumber(tc)} CHF
                  </td>
                  <td className="pl-cell-num text-gray-800">
                    {formatNumber(paid)} CHF
                  </td>
                  <td className="pl-cell-num text-gray-800">
                    {formatNumber(rem)} CHF
                  </td>
                  <td className="pl-cell-center">
                    <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                      <FileText className="h-3.5 w-3.5 text-gray-400" />
                      {v.deal_count}
                    </span>
                  </td>
                  <td className="pl-cell-center">
                    <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                      <Hash className="h-3.5 w-3.5 text-gray-400" />
                      {v.line_item_count}
                    </span>
                  </td>
                  <td className="px-2 text-right">
                    <ChevronRight className="h-4 w-4 text-gray-300" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendorDetailView({
  vendorName,
  paymentLog,
  dealBreakdown,
  onBack,
  loading,
  breakdownLoading,
  paymentLogLoading,
  onPaymentLogged,
  onOpenDealVendorPayments,
  onOpenDeal,
}) {
  const bd = useMemo(() => dealBreakdown || [], [dealBreakdown]);
  const [showChfReference, setShowChfReference] = useState(true);

  const headerAgg = useMemo(() => aggregateVendorDetailHeader(bd, showChfReference), [bd, showChfReference]);
  const byDealTotals = useMemo(() => {
    return bd.reduce(
      (acc, row) => {
        acc.totalCostChfReporting += Number(row.total_cost_chf_reporting ?? row.total_cost_chf ?? 0);
        acc.paidChfReporting += Number(row.paid_chf_reporting ?? row.paid_chf ?? 0);
        acc.remainingChfReporting += vendorRemainingChf(row);
        acc.openReceivablesChf += Number(row.open_receivables_chf ?? 0);
        acc.totalRevenueChfReporting += Number(row.total_revenue_chf_reporting ?? row.total_revenue_chf ?? 0);
        return acc;
      },
      {
        totalCostChfReporting: 0,
        paidChfReporting: 0,
        remainingChfReporting: 0,
        openReceivablesChf: 0,
        totalRevenueChfReporting: 0,
      },
    );
  }, [bd]);
  const vendorFocusedMargin = byDealTotals.openReceivablesChf - byDealTotals.remainingChfReporting;
  const paymentLogTotals = useMemo(() => {
    const amountByCurrency = {};
    let chfReporting = 0;
    for (const entry of paymentLog || []) {
      const currency = String(entry.currency || 'CHF');
      const amount = Number(entry.amount_vendor ?? entry.amount_chf ?? 0);
      amountByCurrency[currency] = Number(amountByCurrency[currency] || 0) + amount;
      chfReporting += Number(entry.amount_chf ?? 0);
    }
    const currencyTotals = Object.entries(amountByCurrency).sort(([a], [b]) => a.localeCompare(b));
    return { currencyTotals, chfReporting };
  }, [paymentLog]);

  const totalCost = headerAgg.total;
  const aggPaid = bd.length > 0 ? headerAgg.paid : 0;
  const aggRem = bd.length > 0 ? headerAgg.rem : 0;
  const headerCur = headerAgg.cur;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50/50 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:px-6 sm:py-4">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-1.5 h-8 text-xs"
          data-testid="vendor-detail-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>
        <div className="hidden h-5 w-px bg-gray-200 sm:block" />
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100">
            <Building2 className="h-4 w-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-gray-900" data-testid="vendor-detail-name">{vendorName}</h2>
            <span className="text-xs text-gray-400">{paymentLog.length} logged payments</span>
          </div>
        </div>
        </div>
        <div className="flex flex-col items-end gap-1 sm:ml-auto">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Total Cost</span>
              <span className="text-lg font-mono font-bold tabular-nums text-gray-900" data-testid="vendor-detail-total">
                {formatNumber(totalCost)} {headerCur}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Paid</span>
              <span className="text-lg font-mono font-bold tabular-nums text-gray-900" data-testid="vendor-detail-paid">
                {formatNumber(aggPaid)} {headerCur}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Remaining</span>
              <span className="text-lg font-mono font-bold tabular-nums text-gray-900" data-testid="vendor-detail-remaining">
                {formatNumber(aggRem)} {headerCur}
              </span>
            </div>
          </div>
          {headerAgg.note ? (
            <p className="max-w-md text-right text-[10px] leading-snug text-amber-800/90">{headerAgg.note}</p>
          ) : null}
        </div>
      </div>

      {/* Vendor details tables */}
      <div className="min-w-0 flex-1 overflow-auto p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400 text-sm">Loading...</div>
        ) : (
          <div className="min-w-0 space-y-8">
          {!breakdownLoading ? (
            <VendorPaymentForm
              vendorName={vendorName}
              deals={dealBreakdown || []}
              onLogged={onPaymentLogged}
              showChfReference={showChfReference}
              onToggleChfReference={() => setShowChfReference((v) => !v)}
              onOpenDealVendorPayments={onOpenDealVendorPayments}
            />
          ) : null}
          <div>
            <h3 className="pl-section-title mb-2">By deal</h3>
            {breakdownLoading ? (
              <p className="text-sm text-gray-400">Loading deal breakdown…</p>
            ) : (dealBreakdown || []).length === 0 ? (
              <p className="text-sm text-gray-400">No deals with this vendor.</p>
            ) : (
              <div className="pl-table-shell overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm" data-testid="vendor-deals-breakdown-table">
                  <thead className="pl-table-header">
                    <tr className="bg-white text-left text-xs text-gray-500">
                      <th className="pl-table-head-cell text-left">Deal</th>
                      <th className="pl-table-head-cell text-right">
                        <span className="block">Deal total revenue</span>
                        <span className="block font-normal normal-case text-[10px] text-gray-400">
                          Deal · CHF reporting
                        </span>
                      </th>
                      <th className="pl-table-head-cell text-right">
                        <span className="block">Total cost</span>
                        <span className="block font-normal normal-case text-[10px] text-gray-400">
                          Vendor · CHF reporting
                        </span>
                      </th>
                      <th className="pl-table-head-cell text-right">
                        <span className="block">Paid</span>
                        <span className="block font-normal normal-case text-[10px] text-gray-400">
                          Vendor · CHF
                        </span>
                      </th>
                      <th className="pl-table-head-cell text-right">
                        <span className="block">Remaining</span>
                        <span className="block font-normal normal-case text-[10px] text-gray-400">
                          Vendor · CHF
                        </span>
                      </th>
                      <th className="pl-table-head-cell text-right">
                        <span className="block">Left to be paid (client)</span>
                        <span className="block font-normal normal-case text-[10px] text-gray-400">
                          Open receivables (CHF)
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dealBreakdown.map((row) => (
                      <tr
                        key={row.deal_id}
                        className={cn(
                          'pl-row border-b border-gray-50',
                          onOpenDeal && 'cursor-pointer hover:bg-sky-50/50',
                        )}
                        onClick={() => onOpenDeal?.(row.deal_id)}
                        onKeyDown={
                          onOpenDeal
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onOpenDeal(row.deal_id);
                                }
                              }
                            : undefined
                        }
                        tabIndex={onOpenDeal ? 0 : undefined}
                        role={onOpenDeal ? 'button' : undefined}
                        aria-label={onOpenDeal ? `Open deal ${row.deal_name}` : undefined}
                        data-testid={`vendor-deal-row-${row.deal_id}`}
                      >
                        <td
                          className={cn(
                            'pl-cell-text',
                            onOpenDeal && 'font-medium text-blue-700 underline decoration-blue-200 underline-offset-2',
                          )}
                        >
                          {row.deal_name}
                        </td>
                        <td className="pl-cell-num align-top">
                          <ByDealRevenueCell row={row} />
                        </td>
                        <td className="pl-cell-num align-top">
                          <ByDealDualAmountCell row={row} kind="total" />
                        </td>
                        <td className="pl-cell-num align-top">
                          <ByDealDualAmountCell row={row} kind="paid" />
                        </td>
                        <td className="pl-cell-num align-top">
                          <ByDealDualAmountCell row={row} kind="rem" />
                        </td>
                        <td className="pl-cell-num align-top">
                          <span className="tabular-nums text-gray-900">
                            {formatNumber(Number(row.open_receivables_chf ?? 0), 2)} CHF
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="h-11 border-t border-gray-200 bg-gray-50/60">
                      <td className="px-4 text-left text-xs font-semibold text-gray-500">Total</td>
                      <td className="px-4 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                        {formatNumber(byDealTotals.totalRevenueChfReporting, 2)} CHF
                      </td>
                      <td className="px-4 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                        {formatNumber(byDealTotals.totalCostChfReporting, 2)} CHF
                      </td>
                      <td className="px-4 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                        {formatNumber(byDealTotals.paidChfReporting, 2)} CHF
                      </td>
                      <td className="px-4 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                        {formatNumber(byDealTotals.remainingChfReporting, 2)} CHF
                      </td>
                      <td className="px-4 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                        <div className="flex flex-col items-end gap-0.5">
                          <span>{formatNumber(byDealTotals.openReceivablesChf, 2)} CHF</span>
                          <span className="text-[11px] font-medium text-blue-700">
                            Margin: {formatNumber(vendorFocusedMargin, 2)} CHF
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="min-w-0 overflow-x-auto">
          <h3 className="pl-section-title mb-2">Payment log</h3>
          {paymentLogLoading ? (
            <p className="text-sm text-gray-400">Loading payment log…</p>
          ) : paymentLog.length === 0 ? (
            <p className="text-sm text-gray-400">No logged payments for this vendor yet.</p>
          ) : (
          <table className="pl-table-shell w-full min-w-[720px] border-separate border-spacing-0" data-testid="vendor-payment-log-table">
            <thead className="pl-table-header">
              <tr className="bg-white">
                <th className="pl-table-head-cell text-left">Deal</th>
                <th className="pl-table-head-cell text-center">Payment date</th>
                <th className="pl-table-head-cell text-right">Amount</th>
                <th className="pl-table-head-cell text-right">CHF reporting</th>
                <th className="pl-table-head-cell text-left">Note</th>
              </tr>
            </thead>
            <tbody>
              {paymentLog.map((entry) => (
                <tr key={entry.payment_id} className="pl-row border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="pl-cell-text">{entry.deal_name}</td>
                  <td className="pl-cell-center tabular-nums">{formatIsoDate(entry.payment_date)}</td>
                  <td className="pl-cell-num font-medium">
                    {formatNumber(entry.amount_vendor ?? entry.amount_chf, 2)} {entry.currency || 'CHF'}
                  </td>
                  <td className="pl-cell-num">{formatNumber(entry.amount_chf, 2)} CHF</td>
                  <td className="pl-cell-text text-gray-600">{entry.note || '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="h-11 border-t border-gray-200 bg-gray-50/60">
                <td className="px-4 text-left text-xs font-semibold text-gray-500">Total</td>
                <td className="px-4" />
                <td className="px-4 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                  <div className="flex flex-col items-end gap-0.5">
                    {paymentLogTotals.currencyTotals.map(([cur, total]) => (
                      <span key={cur}>
                        {formatNumber(total, 2)} {cur}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 text-right text-[13px] font-semibold tabular-nums text-gray-900">
                  {formatNumber(paymentLogTotals.chfReporting, 2)} CHF
                </td>
                <td className="px-4" />
              </tr>
            </tfoot>
          </table>
          )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function VendorTab({ onOpenDealVendorPayments, onOpenDeal }) {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorPaymentLog, setVendorPaymentLog] = useState([]);
  const [dealBreakdown, setDealBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [paymentLogLoading, setPaymentLogLoading] = useState(false);

  const refreshAggregation = useCallback(async () => {
    try {
      const response = await api.getVendorAggregation();
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendor aggregation:', error);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    refreshAggregation().finally(() => setLoading(false));
  }, [refreshAggregation]);

  // Separated from the initial select flow so it can be called after a
  // payment is logged without re-triggering the full "loading spinner" UX.
  const refreshDealBreakdown = useCallback(async (vendorName) => {
    if (!vendorName) return;
    try {
      const bdRes = await api.getVendorDealsBreakdown(vendorName);
      setDealBreakdown(bdRes.data || []);
    } catch (error) {
      console.error('Failed to fetch vendor deals breakdown:', error);
    }
  }, []);

  const handleSelectVendor = useCallback(async (vendorName) => {
    setSelectedVendor(vendorName);
    setDetailLoading(true);
    setBreakdownLoading(true);
    setPaymentLogLoading(true);
    setDealBreakdown([]);
    setVendorPaymentLog([]);
    try {
      const [breakdownRes, paymentLogRes] = await Promise.all([
        api.getVendorDealsBreakdown(vendorName),
        api.getVendorPaymentsLog(vendorName),
      ]);
      setDealBreakdown(breakdownRes.data || []);
      setVendorPaymentLog(paymentLogRes.data || []);
    } catch (error) {
      console.error('Failed to fetch vendor details:', error);
    } finally {
      setDetailLoading(false);
      setBreakdownLoading(false);
      setPaymentLogLoading(false);
    }
  }, []);

  // Called by the inline payment form after a successful log — refresh both
  // the detail breakdown (paid/remaining per deal) and the global aggregation
  // (totals on the list view) so the UI reflects truth immediately.
  const handlePaymentLogged = useCallback(async () => {
    if (!selectedVendor) return;
    await Promise.all([
      refreshDealBreakdown(selectedVendor),
      api.getVendorPaymentsLog(selectedVendor).then((res) => setVendorPaymentLog(res.data || [])),
      refreshAggregation(),
    ]);
  }, [selectedVendor, refreshDealBreakdown, refreshAggregation]);

  const handleBack = useCallback(() => {
    setSelectedVendor(null);
    setVendorPaymentLog([]);
    setDealBreakdown([]);
  }, []);

  if (selectedVendor) {
    return (
      <VendorDetailView
        vendorName={selectedVendor}
        paymentLog={vendorPaymentLog}
        dealBreakdown={dealBreakdown}
        onBack={handleBack}
        loading={detailLoading}
        breakdownLoading={breakdownLoading}
        paymentLogLoading={paymentLogLoading}
        onPaymentLogged={handlePaymentLogged}
        onOpenDealVendorPayments={onOpenDealVendorPayments}
        onOpenDeal={onOpenDeal}
      />
    );
  }

  return (
    <VendorListView
      vendors={vendors}
      onSelectVendor={handleSelectVendor}
      loading={loading}
    />
  );
}
